import { useCallback, useContext, useMemo } from "react";
import { Box } from "@mui/material";
import { useStyles } from "./WeekTrend.styles";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { ILineChartData, IWeekTrend, IWeekTrendAxes } from "./WeekTrendInterfaces";
import { I13WeekTrendDowntimeTotals } from "entities/Mills/MillsEntityInterfaces";
import { getTimeTrends } from "utilities/utilities";
import { translateDefaultEntity } from "utilities/localesUtilities";
import NoData from "components/NoData/NoData.view";
import moment from "moment";
import { LineChart } from "./LineChart/LineChart.view";
import { LineChartsLegends } from "./LineChartLegends/LineChartLegends.view";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { TrendReportContext } from "contexts/TrendReportContext/TrendReportContext";
import { DefaultTranslationFieldsInTable, DisplayInOptionsEnum } from "constants/constants";
import { useTranslation } from "react-i18next";

export const WeekTrend = () => {
    const classes = useStyles();
    const { millId } = useParams();
    const { i18n } = useTranslation();

    const {
        selectedProductionLine,
        selectedMachineCenter,
        selectedFailureType,
        selectedSecondaryFailureCode,
        selectedFailureCode,
    } = useContext(TrendReportContext);

    const { reportRangeEndDate: endDate, displayUnit: displayIn } = useContext(MillDashboardContext);

    const queryFilters = useMemo(
        () => ({
            endDate,
            productionLine: selectedProductionLine?.id,
            machineCenter: selectedMachineCenter?.id,
            failureType: selectedFailureType?.id,
            failureCode: selectedFailureCode?.id,
            secondaryFailureCode: selectedSecondaryFailureCode?.id,
        }),
        [
            endDate,
            selectedProductionLine?.id,
            selectedMachineCenter?.id,
            selectedFailureType?.id,
            selectedFailureCode?.id,
            selectedSecondaryFailureCode?.id,
        ],
    );

    const { downtimes13WeekTrend } = MillsEntity.Get13WeekTrendDowntimeTotals(+millId!, queryFilters);

    const thirteenWeeksFromNow = useMemo(() => (endDate ? getTimeTrends(endDate, 13, 7) : []), [endDate]);

    const getFailureTypeTrendDate = useCallback(
        (date: Date) => {
            for (let i = 0; i < thirteenWeeksFromNow.length; i++) {
                if (date < thirteenWeeksFromNow[i] && date > thirteenWeeksFromNow[i - 1]) {
                    return thirteenWeeksFromNow[i].toISOString();
                }
            }
        },
        [thirteenWeeksFromNow],
    );

    const statsByFailureType: { [key: string]: IWeekTrend } = useMemo(() => {
        const statsObject: { [key: string]: IWeekTrend } = {};

        const totalMinutesByWeek = downtimes13WeekTrend.reduce(
            (accumulator: { [key: string]: number }, currentStat) => {
                const formattedDate = getFailureTypeTrendDate(
                    new Date(moment(new Date(currentStat.week_date)).format("YYYY-MM-DD")),
                );

                if (formattedDate) {
                    if (accumulator[formattedDate]) {
                        accumulator[formattedDate] += Math.round(currentStat.week_seconds / 60);
                    } else {
                        accumulator[formattedDate] = Math.round(currentStat.week_seconds / 60);
                    }
                }
                return accumulator;
            },
            {},
        );

        downtimes13WeekTrend.forEach((stat: I13WeekTrendDowntimeTotals) => {
            const defaultFailureTypeTranslatedName = translateDefaultEntity(
                i18n.language,
                stat.failure_type,
                "name",
                DefaultTranslationFieldsInTable.FAILURE_TYPE,
            );
            const trendDate = getFailureTypeTrendDate(new Date(moment(new Date(stat.week_date)).format("YYYY-MM-DD")));
            if (trendDate) {
                if (Object.keys(statsObject).includes(defaultFailureTypeTranslatedName)) {
                    statsObject[defaultFailureTypeTranslatedName].data.push({
                        x: trendDate,
                        y:
                            displayIn === DisplayInOptionsEnum.MINUTES
                                ? Math.round(stat.week_seconds / 60)
                                : displayIn === DisplayInOptionsEnum.PERCENT
                                ? totalMinutesByWeek[trendDate]
                                    ? Number(
                                          (
                                              (100 * Math.round(stat.week_seconds / 60)) /
                                              totalMinutesByWeek[trendDate]
                                          ).toFixed(1),
                                      )
                                    : 0
                                : stat.week_occurrences,
                    });
                } else {
                    const values: IWeekTrend = {
                        name: defaultFailureTypeTranslatedName,
                        color: stat.failure_type.color_code,
                        data: [
                            {
                                x: trendDate,
                                y:
                                    displayIn === DisplayInOptionsEnum.MINUTES
                                        ? Math.round(stat.week_seconds / 60)
                                        : displayIn === DisplayInOptionsEnum.PERCENT
                                        ? totalMinutesByWeek[trendDate]
                                            ? Number(
                                                  (
                                                      (100 * Math.round(stat.week_seconds / 60)) /
                                                      totalMinutesByWeek[trendDate]
                                                  ).toFixed(1),
                                              )
                                            : 0
                                        : stat.week_occurrences,
                            },
                        ],
                    };

                    statsObject[values.name] = values;
                }
            }
        });

        return statsObject;
    }, [downtimes13WeekTrend, getFailureTypeTrendDate, i18n.language, displayIn]);

    useMemo(() => {
        for (const item in statsByFailureType) {
            const failureTypesStatesDates: string[] = statsByFailureType[item].data.map((dataItem) => dataItem.x);
            thirteenWeeksFromNow.forEach((date: Date) => {
                if (!failureTypesStatesDates.includes(new Date(date).toISOString())) {
                    statsByFailureType[item].data.push({
                        x: new Date(date).toISOString(),
                        y: 0,
                    });
                }
            });

            statsByFailureType[item].data.sort((a, b) => Date.parse(a.x) - Date.parse(b.x));
        }
    }, [thirteenWeeksFromNow, statsByFailureType]);

    const lineChartMemoized = useMemo(() => {
        const lineChartData: ILineChartData[] = [];

        for (const key in statsByFailureType) {
            let summarizedOccurencesByDate: { [key: string]: number } = {};
            statsByFailureType[key].data.forEach((item) => {
                if (summarizedOccurencesByDate[item.x]) {
                    summarizedOccurencesByDate[item.x] += item.y;
                } else {
                    summarizedOccurencesByDate[item.x] = item.y;
                }
            });
            let summarizedOccurencesByDateArray: IWeekTrendAxes[] = [];
            for (const keyDate in summarizedOccurencesByDate) {
                summarizedOccurencesByDateArray = [
                    ...summarizedOccurencesByDateArray,
                    { x: keyDate, y: summarizedOccurencesByDate[keyDate] },
                ];
            }

            const item = {
                id: statsByFailureType[key].name,
                color: statsByFailureType[key].color,
                data: summarizedOccurencesByDateArray,
            };

            lineChartData.push(item);
        }
        return lineChartData;
    }, [statsByFailureType]);

    return (
        <>
            {!lineChartMemoized.length ? (
                <Box className={classes.noData}>
                    <NoData className={classes.noData} />
                </Box>
            ) : (
                <Box className={classes.graphContainer}>
                    <Box className={classes.graphGridItemContainer}>
                        <LineChart data={lineChartMemoized} />
                    </Box>
                    <LineChartsLegends data={lineChartMemoized} />
                </Box>
            )}
        </>
    );
};
